"use client";

import React, {useState, useRef, useEffect} from "react";

import FAQList from "@/components/global/ui/FAQList";
import {cn} from "@/lib/utils";
import Image from "next/image";
import {PortableTextBlock} from "sanity";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";

import dottedArrow from "@/public/global/dotted-arrow.svg";

export default function FAQSection({
  navTitle,
  items,
  glossaryItems,
  className,
  slug
}: {
  navTitle?: string;
  items: {
    title: string;
    faqList: {
      question: string;
      answer: PortableTextBlock[];
    }[];
  }[];
  glossaryItems?: {
    title: string;
    text: PortableTextBlock[];
  }[];
  className?: string;
  slug?: string;
}) {
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = sectionRefs.current.findIndex(ref => ref === entry.target);
            if (index !== -1) {
              setActiveSectionIndex(index);
            }
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '-20% 0px -40% 0px'
      }
    );

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      observer.disconnect();
    };
  }, [items]);

  const scrollToSection = (index: number) => {
    const section = document.getElementById(`faq-section-${index}`);
    if (section) {
      const y = section.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const isBrazil = slug?.endsWith("/br");

  return (
    <section className={cn(
      "relative overflow-clip bg-white border border-[#E5E5E5] rounded-[2.25rem] lg:rounded-[4rem] flex flex-col lg:flex-row",
      className
    )}>
      <div className="w-[min(94.25rem,100%)] lg:w-[min(57.625rem,100%)] px-[2.5rem] sm:px-[4rem] flex flex-col items-center flex-shrink-0 pt-[3rem] lg:pt-[10rem] pb-[6rem] lg:pb-[18rem] relative border-r border-r-[#E5E5E5]">
        <div className="w-full lg:w-[min(34.75rem,100%)] lg:sticky lg:top-[10rem]">
          {navTitle && (
            <p className="mb-[1.5rem] lg:mb-[3.75rem] font-instrument-sans text-[1.5rem] uppercase font-medium text-[#103037] tracking-[.17em]">{navTitle}</p>
          )}

          {items && (
            <>
              {/* Mobile Dropdown Navigation */}
              <div className="lg:hidden w-full">
                <select
                  className="w-[min(57.625rem,100%)] px-[2rem] py-[2.25rem] min-h-[6.75rem] bg-white border border-[#E5E5E5] rounded-[1rem] font-geist text-[2.25rem] text-[#103037] font-medium tracking-tight appearance-none cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#103037]/20"
                  defaultValue={0}
                  onChange={(e) => scrollToSection(Number(e.target.value))}
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0.368591 0.33786L5.86859 6.33786L11.3686 0.33786' stroke='%23103037'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 1.5rem center',
                    backgroundSize: '1.625rem',
                    paddingRight: '2.125rem'
                  }}
                >
                  {items.map((item, index) => (
                    <option
                      key={index}
                      value={index}
                    >
                      {item?.title}
                    </option>
                  ))}

                  {glossaryItems && (
                    <option
                      key={items.length}
                      value={"glossary"}
                    >
                      {isBrazil ? "Glossário" : "Glossary"}
                    </option>
                  )}
                </select>
              </div>

              {/* Desktop Navigation */}
              <div className="hidden lg:flex flex-col items-start gap-[2.5rem]">
                {items.map((item, index) => (
                  <p
                    key={index}
                    onClick={() => scrollToSection(index)}
                    className={cn(
                      "mb-0 leading-none text-[#103037] cursor-pointer transition-opacity duration-300",
                      activeSectionIndex === index ? "opacity-100 font-medium" : "opacity-60 hover:opacity-80 cursor-pointer font-normal"
                    )}>
                    {item?.title}
                  </p>
                ))}

                {glossaryItems && (
                  <p
                    onClick={() => scrollToSection(items.length)}
                    className={cn(
                      "mb-0 leading-none text-[#103037] cursor-pointer transition-opacity duration-300",
                      activeSectionIndex === items.length ? "opacity-100 font-medium" : "opacity-60 font-normal"
                    )}>
                    {isBrazil ? "Glossário" : "Glossary"}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="w-full lg:w-auto lg:grow lg:pb-[2rem] lg:pt-[11.5rem]">
        {items && items.map((item, index) => (
          <div
            key={index}
            id={`faq-section-${index}`}
            ref={(el) => (sectionRefs.current[index] = el)}
            className={cn(
              "flex flex-col items-center",
              index < items.length - 1 && "border-b border-[#E5E5E5]",
              index === 0 ? "pb-[6rem] lg:pb-[12rem]" : "py-[6rem] lg:py-[12rem]"
            )}
          >
            <div className="px-[2.5rem] sm:px-[4rem] w-[min(94.25rem,100%)] flex flex-col items-start gap-[2.5rem] lg:gap-[5rem]">
              <h2 className="mb-0 text-[#103037] text-[2.25rem] sm:text-[3.5rem] lg:text-[4.5rem]">{item?.title}</h2>
              <FAQList faq={item.faqList} />
            </div>
          </div>
        ))}

        {glossaryItems && (
          <div
            id={`faq-section-${items.length}`}
            ref={(el) => (sectionRefs.current[items.length] = el)}
            className="flex flex-col items-center py-[6rem] lg:py-[12rem] border-t border-[#E5E5E5]"
          >
            <div className="px-[2.5rem] sm:px-[4rem] w-[min(94.25rem,100%)] flex flex-col items-start ">
              <h2 className="text-[#103037] text-[2.25rem] sm:text-[3.5rem] lg:text-[4.5rem] mb-[2.5rem] lg:mb-[5rem]">{isBrazil ? "Glossário" : "Glossary"}</h2>

              {glossaryItems.map((item, index) => (
                <div
                  key={index}
                  className="w-full flex flex-col items-start p-[2.25rem] sm:p-[4rem] relative"
                >
                  <div className="flex flex-1 gap-[2rem] sm:gap-[2.5rem] items-center">
                    <Image
                      className="!w-[2.25rem]"
                      src={dottedArrow}
                      alt="dotted arrow"
                    />

                    <h3 className="mb-0 font-geist font-medium text-[#103037] text-start text-[2rem] lg:text-[2.25rem] tracking-normal">
                      {item?.title}
                    </h3>
                  </div>


                  <div className="pb-0 pl-[4.25rem] sm:pl-[4.75rem] pt-[1rem] richtext richtext-start prose-headings:!text-[2.25rem] prose-headings:!mt-[2.5rem] prose-headings:!mb-[.5rem] prose-strong:text-black/90 prose-strong:font-medium prose-p:mb-[1.5rem] prose-ul:mb-[1.5rem] prose-ul:pl-[2rem] prose-li:m-0 prose-li:p-0 prose-ul:flex prose-ul:flex-col prose-ul:gap-[.5rem]">
                    <PortableText
                      value={item?.text}
                      components={richTextComponent}
                    />
                  </div>

                  <hr
                    className={cn(
                      "translate-y-[1px] opacity-10 group-data-[state=open]:!opacity-0 transition-opacity duration-300 ease-in-out absolute bottom-0 left-1/2 -translate-x-1/2 border-t [border-image:repeating-linear-gradient(to_right,rgba(0,0,0,1)_0,rgba(0,0,0,1)_10px,transparent_10px,transparent_20px)_1] w-[calc(100%-2rem)]",
                      glossaryItems.length > index + 1 ? "block" : "hidden"
                    )}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
